<template>
  <el-dialog
    title="风险详情"
    :visible.sync="visible"
    width="920px"
    :close-on-click-modal="false"
  >
    <div class="risk-dialog" v-loading="loading">
      <template v-if="detail.user && detail.user.id">
        <div class="risk-summary">
          <div>
            <strong>{{ detail.user.nickname || detail.user.username || "--" }}</strong>
            <span class="risk-summary__sub">APP：{{ detail.user.app_name || "--" }}</span>
          </div>
          <div class="risk-summary__badges">
            <el-tag :type="getRiskLevelType(detail.risk_profile.risk_level)">
              {{ getRiskLevelLabel(detail.risk_profile.risk_level) }}
            </el-tag>
            <el-tag type="info">分数：{{ detail.risk_profile.risk_score || 0 }}</el-tag>
          </div>
        </div>

        <el-descriptions :column="2" border class="risk-block">
          <el-descriptions-item label="运营备注" :span="2">
            {{ detail.risk_profile.risk_remark_summary || "--" }}
          </el-descriptions-item>
          <el-descriptions-item label="是否风险用户">
            {{ detail.risk_profile.is_risk_user ? "是" : "否" }}
          </el-descriptions-item>
          <el-descriptions-item label="跨App设备">
            {{ detail.risk_profile.cross_app_device_hit ? "是" : "否" }}
          </el-descriptions-item>
          <el-descriptions-item label="跨App数量">
            {{ detail.risk_profile.cross_app_app_count || 0 }}
          </el-descriptions-item>
          <el-descriptions-item label="最近设备预警">
            {{ formatTime(detail.risk_profile.recent_alert_time) }}
          </el-descriptions-item>
          <el-descriptions-item label="风险标签" :span="2">
            {{ formatRiskTags(detail.risk_profile.risk_tags) }}
          </el-descriptions-item>
          <el-descriptions-item label="涉及App" :span="2">
            {{ formatCrossApps(detail.risk_profile.cross_app_apps) }}
          </el-descriptions-item>
        </el-descriptions>

        <el-card shadow="never" class="risk-block">
          <div slot="header" class="risk-card-title">评分拆解</div>
          <el-table v-if="!isMobileViewport" :data="detail.risk_profile.score_breakdown || []" border stripe size="mini">
            <el-table-column prop="label" label="加分项" min-width="160" />
            <el-table-column prop="description" label="原因" min-width="240" />
            <el-table-column prop="current_value" label="当前值" min-width="120" />
            <el-table-column prop="extra_text" label="补充说明" min-width="180" />
            <el-table-column prop="score" label="分值" width="100" align="center" />
          </el-table>
          <mobile-record-list
            v-else
            :rows="detail.risk_profile.score_breakdown || []"
            :title="getMobileScoreTitle"
            :subtitle="getMobileScoreSubtitle"
            :fields="mobileScoreFields"
          />
        </el-card>

        <el-card shadow="never" class="risk-block">
          <div slot="header" class="risk-card-title">统一处置记录</div>
          <el-table v-if="!isMobileViewport" :data="riskActionList" border stripe size="mini">
            <el-table-column label="时间" min-width="160" align="center">
              <template slot-scope="{ row }">
                {{ formatTime(row.created_time) }}
              </template>
            </el-table-column>
            <el-table-column label="来源类型" width="110" align="center">
              <template slot-scope="{ row }">
                {{ getRiskSourceLabel(row.source_type) }}
              </template>
            </el-table-column>
            <el-table-column label="来源规则" min-width="120" align="center">
              <template slot-scope="{ row }">
                {{ getRiskSourceRuleTypeLabel(row.source_rule_type) }}
              </template>
            </el-table-column>
            <el-table-column label="渠道" min-width="120" align="center">
              <template slot-scope="{ row }">
                {{ row.channel_name || "--" }}
              </template>
            </el-table-column>
            <el-table-column label="策略/规则名称" min-width="160" align="center">
              <template slot-scope="{ row }">
                {{ row.policy_name || "--" }}
              </template>
            </el-table-column>
            <el-table-column label="动作" width="100" align="center">
              <template slot-scope="{ row }">
                {{ getActionLabel(row.action) }}
              </template>
            </el-table-column>
            <el-table-column label="风险等级" width="110" align="center">
              <template slot-scope="{ row }">
                <el-tag v-if="row.risk_level" :type="getRiskLevelType(row.risk_level)" size="mini">
                  {{ getRiskLevelLabel(row.risk_level) }}
                </el-tag>
                <span v-else>--</span>
              </template>
            </el-table-column>
            <el-table-column label="风险分数" width="90" align="center">
              <template slot-scope="{ row }">
                {{ row.risk_score || 0 }}
              </template>
            </el-table-column>
            <el-table-column label="状态" width="100" align="center">
              <template slot-scope="{ row }">
                <el-tag
                  v-if="row.status"
                  :type="getTaskRunStatusType(row.status)"
                  size="mini"
                >
                  {{ getTaskRunStatusLabel(row.status) }}
                </el-tag>
                <span v-else>--</span>
              </template>
            </el-table-column>
            <el-table-column prop="remark" label="备注" min-width="220" />
          </el-table>
          <mobile-record-list
            v-else
            :rows="riskActionList"
            :title="getMobileRiskActionTitle"
            :subtitle="getMobileRiskActionSubtitle"
            :fields="mobileRiskActionFields"
          />
          <el-empty v-if="!riskActionList.length" description="暂无处置记录" />
        </el-card>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import {
  getActionLabel,
  getRiskLevelLabel,
  getRiskLevelType,
  getRiskSourceLabel,
  getRiskSourceRuleTypeLabel,
  getTaskRunStatusLabel,
  getTaskRunStatusType,
} from "@/utils/riskTask";
import responsiveMixin from "@/mixins/responsive";

export default {
  mixins: [responsiveMixin],
  data() {
    return {
      visible: false,
      loading: false,
      detail: {
        user: null,
        risk_profile: {},
      },
      riskActionList: [],
    };
  },
  computed: {
    mobileScoreFields() {
      return [
        { label: "原因", key: "description" },
        { label: "当前值", key: "current_value" },
        { label: "分值", key: "score" },
        { label: "补充说明", key: "extra_text" },
      ];
    },
    mobileRiskActionFields() {
      return [
        { label: "来源类型", value: (row) => this.getRiskSourceLabel(row.source_type) },
        { label: "动作", value: (row) => this.getActionLabel(row.action) },
        { label: "风险等级", value: (row) => this.getRiskLevelLabel(row.risk_level) },
        { label: "时间", value: (row) => this.formatTime(row.created_time) },
        { label: "来源规则", value: (row) => this.getRiskSourceRuleTypeLabel(row.source_rule_type), summary: false },
        { label: "渠道", key: "channel_name", summary: false },
        { label: "策略/规则名称", key: "policy_name", summary: false },
        { label: "风险分数", key: "risk_score", summary: false },
        { label: "状态", value: (row) => this.getTaskRunStatusLabel(row.status), summary: false },
        { label: "备注", key: "remark", summary: false },
      ];
    },
  },
  methods: {
    getMobileScoreTitle(row) {
      return row.label || "评分项";
    },
    getMobileScoreSubtitle(row) {
      return `分值 ${row.score || 0}`;
    },
    getMobileRiskActionTitle(row) {
      return row.policy_name || this.getActionLabel(row.action) || "处置记录";
    },
    getMobileRiskActionSubtitle(row) {
      return this.formatTime(row.created_time);
    },
    getActionLabel,
    getRiskLevelLabel,
    getRiskLevelType,
    getRiskSourceLabel,
    getRiskSourceRuleTypeLabel,
    getTaskRunStatusLabel,
    getTaskRunStatusType,
    async open(id) {
      this.visible = true;
      this.loading = true;
      try {
        const [riskInfo, riskActionList] = await Promise.all([
          this.$api.getUser.getRiskInfo({ id }),
          this.$api.getUser.getRiskActionList({ id, limit: 50 }),
        ]);
        this.detail = riskInfo.data || {
          user: null,
          risk_profile: {},
        };
        this.riskActionList = Array.isArray(riskActionList.data) ? riskActionList.data : [];
      } finally {
        this.loading = false;
      }
    },
    formatRiskTags(tags) {
      if (!Array.isArray(tags) || !tags.length) {
        return "--";
      }
      return tags.join(" / ");
    },
    formatCrossApps(apps) {
      if (!Array.isArray(apps) || !apps.length) {
        return "--";
      }
      return apps.join(" / ");
    },
    formatTime(value) {
      return value ? this.$fnc.getTimeFormat(value, "ymdhms") : "--";
    },
  },
};
</script>

<style lang="less" scoped>
.risk-dialog {
  min-height: 120px;
}

.risk-summary {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.risk-summary__sub {
  margin-left: 12px;
  color: #6f84a8;
  font-size: 13px;
}

.risk-summary__badges {
  display: flex;
  gap: 8px;
}

.risk-block {
  margin-top: 16px;
}

.risk-card-title {
  font-weight: 600;
  color: #203864;
}
</style>
